import type { PageviewEvent } from '@logclip/types';
import type { InstrumentCtx, Teardown } from './context.js';
import { uuid } from './util.js';
import { maskString, redactUrl } from './mask.js';

/**
 * Auto-captures a `pageview` event on the initial load and on every SPA route
 * change — history.pushState / replaceState (patched, since neither fires a
 * native event), popstate (back/forward), and hashchange.
 *
 * The "page" identity is the pathname, plus a hash-router segment when the hash
 * looks like a route (`#/checkout`) but NOT a plain anchor (`#section`) — so
 * anchor jumps don't masquerade as page views. Same-path repeats are de-duped.
 * Each view records the previous in-app path as its `referrer`: the "from" edge
 * that Path / Journey analysis chains.
 */
export function instrumentPageviews(ctx: InstrumentCtx): Teardown {
  let lastPath: string | null = null;

  const currentPath = (): string => {
    const hash = location.hash;
    return location.pathname + (hash.startsWith('#/') ? hash : '');
  };

  const fire = () => {
    const path = currentPath();
    if (path === lastPath) return; // de-dupe no-op navigations / anchor jumps
    const referrer = lastPath ?? '';
    lastPath = path;
    const e: PageviewEvent = {
      id: uuid(),
      sessionId: ctx.sessionId,
      type: 'pageview',
      t: ctx.clock.now(),
      ts: ctx.clock.wall(),
      url: redactUrl(location.href),
      path,
      // F9: titles carry exactly what input masking works to keep out —
      // "Invoice 4021 — Jane Doe". Same pipeline as console arguments.
      title: document.title ? maskString(document.title) : undefined,
      referrer: referrer || undefined,
    };
    ctx.emit(e);
    ctx.markActivity();
  };

  // Patch history so SPA navigations (which fire no native event) are observed.
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  const wrap = (orig: History['pushState']): History['pushState'] =>
    function patched(this: History, data, unused, url) {
      const ret = orig.call(this, data, unused, url);
      // Defer one microtask so location.* already reflects the new URL.
      queueMicrotask(fire);
      return ret;
    };
  history.pushState = wrap(origPush);
  history.replaceState = wrap(origReplace);
  window.addEventListener('popstate', fire);
  window.addEventListener('hashchange', fire);

  fire(); // the entry view

  return () => {
    history.pushState = origPush;
    history.replaceState = origReplace;
    window.removeEventListener('popstate', fire);
    window.removeEventListener('hashchange', fire);
  };
}
